import z from "zod";

const BaseDbSchema = z.object({
	id: z.string(),
	updated_at: z.coerce.date(),
	created_at: z.coerce.date(),
});

const OffchainFantasyLeagueBetPickDbSchema = z.object({
	event_market_id: z.string(),
	outcome_id: z.string(),
});

const OffchainFantasyLeagueBetTransactionInputBaseSchema = z.object({
	user_id: z.string(),
	offchain_fantasy_league_participant_id: z.string(),
	offchain_fantasy_league_id: z.string().optional(),
	size_raw: z.coerce.bigint(),
	size: z.number(),
	picks: z.array(OffchainFantasyLeagueBetPickDbSchema),
});

const OffchainFantasyLeaguePowerplayBetTransactionInputSchema = OffchainFantasyLeagueBetTransactionInputBaseSchema.merge(z.object({
	type: z.literal("powerplay"),
	combo_multipliers_config_id: z.string(),
}));

const OffchainFantasyLeagueFlexplayBetTransactionInputSchema = OffchainFantasyLeagueBetTransactionInputBaseSchema.merge(z.object({
	type: z.literal("flexplay"),
	flex_combo_multipliers_config_id: z.string(),
}));

export const OffchainFantasyLeagueBetTransactionInputSchema = z.discriminatedUnion("type", [
	OffchainFantasyLeaguePowerplayBetTransactionInputSchema,
	OffchainFantasyLeagueFlexplayBetTransactionInputSchema,
]);

export const OffchainFantasyLeagueDbSchema = BaseDbSchema.merge(z.object({
	title: z.string(),
	short_id: z.string().min(1),
	creator_user_id: z.string(),
	idempotency_key: z.string(),
	sport_id: z.string(),
	league_id: z.string(),
	max_members_count: z.number().int().positive(),
	status: z.enum(["active", "ended"]).default("active"),
	metadata: z.object({
		settler_repeat_after: z.coerce.date().optional(),
		settler_postpones_count: z.number().optional(),
		settler_latest_game_commence_date: z.coerce.date().optional(),
		settler_latest_active_game_commence_date: z.coerce.date().optional(),
		settler_active_games_count: z.number().optional(),
		settler_active_bets_count: z.number().optional(),
		settler_last_checked_at: z.coerce.date().optional(),
	}).default({}),
}));

export const OffchainFantasyLeagueParticipantDbSchema = BaseDbSchema.merge(z.object({
	offchain_fantasy_league_id: z.string(),
	user_id: z.string(),
	available_balance_amount_raw: z.coerce.bigint(),
	available_balance_amount: z.number(),
	reserved_balance_amount_raw: z.coerce.bigint(),
	reserved_balance_amount: z.number(),
	pending_transactions: z.array(z.object({
		id: z.string(),
		offchain_fantasy_league_bet_id: z.string().optional(),
		details: z.string(),
		status: z.enum(["pending", "completed", "expired", "reverted"]),
		amount_raw: z.coerce.bigint(),
		amount: z.number(),
		body: z.discriminatedUnion("type", [
			z.object({
				type: z.literal("offchain-fantasy-league-created"),
				title: z.string(),
				short_id: z.string(),
				sport_id: z.string(),
				league_id: z.string(),
				max_members_count: z.number().int().positive(),
			}),
			z.object({
				type: z.literal("bet-placed"),
				bet: OffchainFantasyLeagueBetTransactionInputSchema,
			}),
		]),
		created_at: z.coerce.date(),
		expires_at: z.coerce.date(),
		resolved_at: z.coerce.date().nullable().optional(),
	})).default([]),
	metadata: z.object({

	}).default({}),
}));

export const OffchainFantasyLeagueBetStatusSchema = z.enum([
	"active",
	"settled",
	"canceled",
	"refunded",
]);

const OffchainFantasyLeagueBetDbBaseSchema = z.object({
	user_id: z.string(),
	offchain_fantasy_league_participant_id: z.string(),
	offchain_fantasy_league_id: z.string().optional(),
	size_raw: z.coerce.bigint(),
	size: z.number(),
	picks: z.array(OffchainFantasyLeagueBetPickDbSchema),
	placed_at: z.coerce.date(),
	status: OffchainFantasyLeagueBetStatusSchema,
	settlement_info: z.object({
		payout_amount_raw: z.coerce.bigint(),
		payout_amount: z.number(),
	}).optional().nullable(),
	metadata: z.object({
		offchain_settler_repeat_after: z.coerce.date().optional(),
		offchain_settler_postpones_count: z.number().optional(),
	}).default({}),
});

const OffchainFantasyLeaguePowerplayBetDbSchema = OffchainFantasyLeagueBetDbBaseSchema.merge(z.object({
	type: z.literal("powerplay"),
	combo_multipliers_config_id: z.string(),
})).merge(BaseDbSchema);

const OffchainFantasyLeagueFlexplayBetDbSchema = OffchainFantasyLeagueBetDbBaseSchema.merge(z.object({
	type: z.literal("flexplay"),
	flex_combo_multipliers_config_id: z.string(),
})).merge(BaseDbSchema);

export const OffchainFantasyLeagueBetDbSchema = z.discriminatedUnion("type", [
	OffchainFantasyLeaguePowerplayBetDbSchema,
	OffchainFantasyLeagueFlexplayBetDbSchema,
]);

export const OffchainFantasyLeagueBalanceTransactionBodySchema = z.discriminatedUnion("type", [
	z.object({
		type: z.literal("participant-initial-top-up"),
	}),
	z.object({
		type: z.literal("bet-placed"),
		bet: OffchainFantasyLeagueBetTransactionInputSchema,
	}),
	z.object({
		type: z.literal("bet-settled"),
		payout_amount_raw: z.coerce.bigint(),
		payout_amount: z.number(),
	}),
	z.object({
		type: z.literal("bet-refunded"),
		fee_amount_raw: z.coerce.bigint(),
		fee_amount: z.number(),
		payout_amount_raw: z.coerce.bigint(),
		payout_amount: z.number(),
	}),
	z.object({
		type: z.literal("bet-canceled"),
		fee_amount_raw: z.coerce.bigint(),
		fee_amount: z.number(),
		payout_amount_raw: z.coerce.bigint(),
		payout_amount: z.number(),
	}),
	z.object({
		type: z.literal("offchain-fantasy-league-settled"),
		fee_amount_raw: z.coerce.bigint(),
		fee_amount: z.number(),
		payout_amount_raw: z.coerce.bigint(),
		payout_amount: z.number(),
	}),
	z.object({
		type: z.literal("transfer"),
		counterparty_user_id: z.string(),
		counterparty_offchain_fantasy_league_participant_id: z.string(),
	}),
]);

export const OffchainFantasyLeagueBalanceTransactionDbSchema = BaseDbSchema.merge(z.object({
	user_id: z.string(),
	offchain_fantasy_league_participant_id: z.string(),
	offchain_fantasy_league_id: z.string().optional(),
	offchain_fantasy_league_bet_id: z.string().optional(),
	details: z.string(),
	amount_raw: z.coerce.bigint(),
	amount: z.number(),
	body: OffchainFantasyLeagueBalanceTransactionBodySchema,
	metadata: z.object({

	}).default({}),
}));
